import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ApiManagement = () => {
  const [apiKeys, setApiKeys] = useState([
    {
      id: 1,
      name: 'Trading Bot Alpha',
      key: 'ak_live_1234567890abcdef',
      secret: '••••••••••••••••••••••••••••••••',
      permissions: ['read', 'trade'],
      status: 'active',
      createdAt: '2024-01-10T10:00:00Z',
      lastUsed: '2024-01-15T14:30:00Z',
      ipWhitelist: ['192.168.1.100', '10.0.0.50']
    },
    {
      id: 2,
      name: 'Portfolio Tracker',
      key: 'ak_live_abcdef1234567890',
      secret: '••••••••••••••••••••••••••••••••',
      permissions: ['read'],
      status: 'active',
      createdAt: '2024-01-05T15:20:00Z',
      lastUsed: '2024-01-15T12:15:00Z',
      ipWhitelist: []
    },
    {
      id: 3,
      name: 'Test Environment',
      key: 'ak_test_9876543210fedcba',
      secret: '••••••••••••••••••••••••••••••••',
      permissions: ['read', 'trade'],
      status: 'inactive',
      createdAt: '2024-01-01T09:00:00Z',
      lastUsed: '2024-01-12T16:45:00Z',
      ipWhitelist: ['127.0.0.1']
    }
  ]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [newApiKey, setNewApiKey] = useState({
    name: '',
    permissions: [],
    ipWhitelist: '',
    environment: 'live'
  });

  const permissionOptions = [
    { value: 'read', label: 'Read', description: 'View account information and trading data' },
    { value: 'trade', label: 'Trade', description: 'Place and manage orders' },
    { value: 'withdraw', label: 'Withdraw', description: 'Withdraw funds from account' }
  ];

  const handleCreateApiKey = () => {
    const newKey = {
      id: Date.now(),
      name: newApiKey.name,
      key: `ak_${newApiKey.environment}_${Math.random().toString(36).substr(2, 16)}`,
      secret: Math.random().toString(36).substr(2, 32),
      permissions: newApiKey.permissions,
      status: 'active',
      createdAt: new Date().toISOString(),
      lastUsed: null,
      ipWhitelist: newApiKey.ipWhitelist.split(',').map(ip => ip.trim()).filter(ip => ip)
    };

    setApiKeys(prev => [...prev, newKey]);
    setSelectedApiKey(newKey);
    setShowCreateModal(false);
    setShowSecretModal(true);
    setNewApiKey({ name: '', permissions: [], ipWhitelist: '', environment: 'live' });
  };

  const handleDeleteApiKey = (keyId) => {
    setApiKeys(prev => prev.filter(key => key.id !== keyId));
  };

  const handleToggleStatus = (keyId) => {
    setApiKeys(prev => prev.map(key => 
      key.id === keyId 
        ? { ...key, status: key.status === 'active' ? 'inactive' : 'active' }
        : key
    ));
  };

  const handlePermissionToggle = (permission) => {
    setNewApiKey(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : 'Never';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading-semibold text-text-primary mb-1">API Management</h2>
          <p className="text-sm text-text-secondary">Create and manage API keys for programmatic trading</p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-trading hover:bg-primary-600 transition-colors duration-trading-fast"
        >
          <Icon name="Plus" size={16} />
          <span className="font-body-medium">Create API Key</span>
        </button>
      </div>

      {/* API Keys List */}
      <div className="space-y-4 mb-8">
        {apiKeys.map((apiKey) => (
          <div key={apiKey.id} className="p-6 border border-border rounded-trading">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-heading-semibold text-text-primary">{apiKey.name}</h3>
                  <span className={`px-2 py-1 rounded-trading text-xs font-body-medium ${
                    apiKey.status === 'active' ?'bg-success-100 text-success' :'bg-secondary-100 text-text-secondary'
                  }`}>
                    {apiKey.status}
                  </span>
                  <span className={`px-2 py-1 rounded-trading text-xs font-body-medium ${
                    apiKey.key.includes('live') 
                      ? 'bg-primary-100 text-primary' :'bg-warning-100 text-warning'
                  }`}>
                    {apiKey.key.includes('live') ? 'Live' : 'Test'}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-secondary">API Key:</span>
                    <div className="flex items-center space-x-2 mt-1">
                      <code className="font-data text-text-primary bg-secondary-50 px-2 py-1 rounded">
                        {apiKey.key}
                      </code>
                      <button
                        onClick={() => copyToClipboard(apiKey.key)}
                        className="text-text-secondary hover:text-text-primary"
                      >
                        <Icon name="Copy" size={14} />
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-text-secondary">Permissions:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {apiKey.permissions.map(permission => (
                        <span key={permission} className="px-2 py-1 bg-accent-100 text-accent text-xs rounded">
                          {permission}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-text-secondary">Created:</span>
                    <p className="text-text-primary mt-1">{formatDate(apiKey.createdAt)}</p>
                  </div>
                  
                  <div>
                    <span className="text-text-secondary">Last Used:</span>
                    <p className="text-text-primary mt-1">{formatDate(apiKey.lastUsed)}</p>
                  </div>
                </div>
                
                {apiKey.ipWhitelist.length > 0 && (
                  <div className="mt-3">
                    <span className="text-text-secondary text-sm">IP Whitelist:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {apiKey.ipWhitelist.map((ip, index) => (
                        <code key={index} className="font-data text-xs text-text-primary bg-secondary-50 px-2 py-1 rounded">
                          {ip}
                        </code>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => handleToggleStatus(apiKey.id)}
                  className={`px-3 py-1 text-sm rounded-trading transition-colors duration-trading-fast ${
                    apiKey.status === 'active' ?'text-warning hover:bg-warning-50' :'text-success hover:bg-success-50'
                  }`}
                >
                  {apiKey.status === 'active' ? 'Disable' : 'Enable'}
                </button>
                
                <button
                  onClick={() => handleDeleteApiKey(apiKey.id)}
                  className="px-3 py-1 text-sm text-error hover:bg-error-50 rounded-trading transition-colors duration-trading-fast"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* API Documentation */}
      <div className="p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-4">API Documentation</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-body-medium text-text-primary mb-3">Getting Started</h4>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li className="flex items-center space-x-2">
                <Icon name="Book" size={14} />
                <a href="#" className="text-primary hover:text-primary-600">API Documentation</a>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="Code" size={14} />
                <a href="#" className="text-primary hover:text-primary-600">Code Examples</a>
              </li>
              <li className="flex items-center space-x-2">
                <Icon name="MessageCircle" size={14} />
                <a href="#" className="text-primary hover:text-primary-600">Developer Support</a>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-body-medium text-text-primary mb-3">Rate Limits</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-secondary">Public Endpoints:</span>
                <span className="text-text-primary">1000 requests/minute</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Private Endpoints:</span>
                <span className="text-text-primary">100 requests/minute</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-secondary">Trading Endpoints:</span>
                <span className="text-text-primary">50 requests/minute</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal">
          <div className="bg-surface border border-border rounded-trading p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-heading-semibold text-text-primary">Create API Key</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-text-secondary hover:text-text-primary"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  API Key Name *
                </label>
                <input
                  type="text"
                  value={newApiKey.name}
                  onChange={(e) => setNewApiKey(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
                  placeholder="Enter a descriptive name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Environment
                </label>
                <select
                  value={newApiKey.environment}
                  onChange={(e) => setNewApiKey(prev => ({ ...prev, environment: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
                >
                  <option value="live">Live Trading</option>
                  <option value="test">Test Environment</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-3">
                  Permissions
                </label>
                <div className="space-y-3">
                  {permissionOptions.map(permission => (
                    <label key={permission.value} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newApiKey.permissions.includes(permission.value)}
                        onChange={() => handlePermissionToggle(permission.value)}
                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary-100 mt-0.5"
                      />
                      <div>
                        <div className="font-body-medium text-text-primary">{permission.label}</div>
                        <div className="text-xs text-text-secondary">{permission.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  IP Whitelist (Optional)
                </label>
                <input
                  type="text"
                  value={newApiKey.ipWhitelist}
                  onChange={(e) => setNewApiKey(prev => ({ ...prev, ipWhitelist: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
                  placeholder="192.168.1.1, 10.0.0.1"
                />
                <p className="text-xs text-text-secondary mt-1">
                  Comma-separated list of IP addresses. Leave empty to allow all IPs.
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-border text-text-secondary rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateApiKey}
                disabled={!newApiKey.name || newApiKey.permissions.length === 0}
                className="px-4 py-2 bg-primary text-white rounded-trading hover:bg-primary-600 transition-colors duration-trading-fast disabled:opacity-50"
              >
                Create API Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Secret Display Modal */}
      {showSecretModal && selectedApiKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal">
          <div className="bg-surface border border-border rounded-trading p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-heading-semibold text-text-primary">API Key Created</h3>
              <button
                onClick={() => setShowSecretModal(false)}
                className="text-text-secondary hover:text-text-primary"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-warning-50 border border-warning-200 rounded-trading">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="AlertTriangle" size={16} className="text-warning" />
                  <span className="font-body-medium text-warning">Important!</span>
                </div>
                <p className="text-sm text-warning">
                  This is the only time you'll see the secret key. Please copy and store it securely.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  API Key
                </label>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 font-data text-sm text-text-primary bg-secondary-50 px-3 py-2 rounded border">
                    {selectedApiKey.key}
                  </code>
                  <button
                    onClick={() => copyToClipboard(selectedApiKey.key)}
                    className="p-2 text-text-secondary hover:text-text-primary border border-border rounded"
                  >
                    <Icon name="Copy" size={16} />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Secret Key
                </label>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 font-data text-sm text-text-primary bg-secondary-50 px-3 py-2 rounded border">
                    {selectedApiKey.secret}
                  </code>
                  <button
                    onClick={() => copyToClipboard(selectedApiKey.secret)}
                    className="p-2 text-text-secondary hover:text-text-primary border border-border rounded"
                  >
                    <Icon name="Copy" size={16} />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end mt-6">
              <button
                onClick={() => setShowSecretModal(false)}
                className="px-4 py-2 bg-primary text-white rounded-trading hover:bg-primary-600 transition-colors duration-trading-fast"
              >
                I've Saved the Keys
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiManagement;